import { Link } from "react-router-dom";
import { useMemo } from "react";
import { articles, categories, hotTags } from "@/mocks/forum";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import CategoryTabs from "@/components/base/CategoryTabs";
import ArticleCard from "@/components/base/ArticleCard";

export default function Home() {
  const pinnedArticles = useMemo(() => articles.filter((a) => a.isPinned), []);
  const hotArticles = useMemo(() => articles.filter((a) => a.isHot && !a.isPinned).slice(0, 3), []);
  const latestArticles = useMemo(() => articles.filter((a) => !a.isPinned && !a.isHot).slice(0, 6), []);
  const sidebarArticles = useMemo(() => articles.slice(0, 8), []);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "楼凤论坛",
    url: "https://www.loufengbbs.com",
    description: "楼凤论坛是汇聚用户真实体验、资讯分享与社区交流的综合性论坛平台。",
    potentialAction: {
      "@type": "SearchAction",
      target: "https://www.loufengbbs.com/search?q={search_term_string}",
      "query-input": "required name=search_term_string",
    },
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />

      <Navbar />

      <main>
        <section className="relative w-full h-[520px] md:h-[600px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20community%20forum%20digital%20platform%20abstract%20background%20with%20connected%20nodes%20warm%20neutral%20gradient%20subtle%20geometric%20patterns%20clean%20minimalist%20atmosphere%20soft%20lighting%20professional%20tech%20aesthetic&width=1600&height=900&seq=hero-forum-2024&orientation=landscape"
            alt="楼凤论坛 - 用户交流与资讯聚合平台"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/25 to-black/50"></div>
          <div className="relative z-10 w-full px-4 md:px-6 text-center">
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 tracking-tight leading-tight">
              楼凤论坛
            </h1>
            <p className="text-base md:text-lg text-white/80 mb-8 max-w-2xl mx-auto leading-relaxed">
              汇聚用户真实体验、资讯分享与社区交流的综合性论坛平台。
              在这里，自由发帖、交流心得、获取最新资讯，打造安全活跃的社区生态。
            </p>
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <Link
                to="/category/latest-news"
                className="px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                浏览资讯
              </Link>
              <Link
                to="/category/user-sharing"
                className="px-6 py-3 bg-background-50/20 backdrop-blur-sm text-white border border-white/30 rounded-full text-sm font-semibold hover:bg-background-50/30 transition-colors cursor-pointer whitespace-nowrap"
              >
                用户分享
              </Link>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-8 bg-background-50">
          <CategoryTabs />
        </section>

        <section className="w-full px-4 md:px-6 pb-8 bg-background-50">
          <div className="flex flex-col lg:flex-row gap-8">
            <div className="flex-1 min-w-0">
              {pinnedArticles.length > 0 && (
                <div className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <i className="ri-pushpin-line text-accent-500 text-lg"></i>
                    <h2 className="text-lg font-semibold text-foreground-950">置顶推荐</h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {pinnedArticles.map((article) => (
                      <ArticleCard key={article.id} article={article} variant="featured" />
                    ))}
                  </div>
                </div>
              )}

              {hotArticles.length > 0 && (
                <div className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <i className="ri-fire-line text-red-500 text-lg"></i>
                    <h2 className="text-lg font-semibold text-foreground-950">热门讨论</h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {hotArticles.map((article) => (
                      <ArticleCard key={article.id} article={article} variant="featured" />
                    ))}
                  </div>
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <i className="ri-time-line text-secondary-500 text-lg"></i>
                    <h2 className="text-lg font-semibold text-foreground-950">最新文章</h2>
                  </div>
                  <Link
                    to="/category/latest-news"
                    className="text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer flex items-center gap-1"
                  >
                    查看更多 <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
                <div className="flex flex-col gap-3">
                  {latestArticles.map((article) => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </div>
              </div>
            </div>

            <aside className="w-full lg:w-[320px] shrink-0">
              <div className="sticky top-20 space-y-6">
                <div className="rounded-xl bg-background-50 border border-background-200/70 p-5">
                  <h3 className="text-sm font-semibold text-foreground-950 mb-4 flex items-center gap-2">
                    <i className="ri-bar-chart-2-line text-secondary-500"></i>
                    热门排行
                  </h3>
                  <div className="flex flex-col gap-1">
                    {sidebarArticles.map((article, idx) => (
                      <Link
                        key={article.id}
                        to={`/article/${article.id}`}
                        className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-background-100 transition-colors cursor-pointer group"
                      >
                        <span
                          className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                            idx < 3
                              ? "bg-accent-500 text-background-50"
                              : "bg-background-100 text-foreground-500"
                          }`}
                        >
                          {idx + 1}
                        </span>
                        <span className="text-sm text-foreground-700 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
                          {article.title}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>

                <div className="rounded-xl bg-background-50 border border-background-200/70 p-5">
                  <h3 className="text-sm font-semibold text-foreground-950 mb-4 flex items-center gap-2">
                    <i className="ri-hashtag text-secondary-500"></i>
                    热门标签
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {hotTags.map((tag) => (
                      <Link
                        key={tag}
                        to={`/category/latest-news`}
                        className="px-3 py-1.5 rounded-full text-xs bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors cursor-pointer"
                      >
                        {tag}
                      </Link>
                    ))}
                  </div>
                </div>

                <div className="rounded-xl bg-background-50 border border-background-200/70 p-5">
                  <h3 className="text-sm font-semibold text-foreground-950 mb-4 flex items-center gap-2">
                    <i className="ri-folder-line text-secondary-500"></i>
                    分类导航
                  </h3>
                  <div className="flex flex-col gap-1">
                    {categories.map((cat) => (
                      <Link
                        key={cat.slug}
                        to={`/category/${cat.slug}`}
                        className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-background-100 transition-colors cursor-pointer group"
                      >
                        <span className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center group-hover:bg-primary-100 transition-colors">
                          <i className={`${cat.icon} text-foreground-600 group-hover:text-primary-600 text-sm`}></i>
                        </span>
                        <div>
                          <span className="text-sm font-medium text-foreground-800">{cat.name}</span>
                          <p className="text-xs text-foreground-400">{cat.description}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}